import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { TabsContent } from '../ui/tabs';
import { Edit, Trash2, Plus, FileText, FileCheck, Mail, Pencil, MapPin, Clock, BookOpen } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { ListPager, usePagedList } from './ListPager';
import { ListToolbar, useFilteredList } from './ListToolbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// パターンの集合時間表示 = 時間割の初日1限開始時刻の10分前（無ければ登録値の時刻部分）
const patternMeetingTime = (tpl) => {
  const src = tpl?.timetable?.[0]?.slots?.[0]?.time || '';
  const m = src.match(/(\d{1,2})[:：](\d{2})/);
  if (m) {
    let total = parseInt(m[1], 10) * 60 + parseInt(m[2], 10) - 10;
    if (total < 0) total = 0;
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  }
  const parts = (tpl?.meetingTime || '').trim().split(/\s+/);
  return parts[parts.length - 1] || '';
};

export const LANG_LABELS_JA = { ja: '日本語', en: '英語', pt: 'ポルトガル語', es: 'スペイン語', vi: 'ベトナム語', id: 'インドネシア語', tl: 'タガログ語', zh: '中国語' };
export const LangCounts = ({ codes, label, testId }) => {
  const counts = {};
  codes.forEach(c => { const k = c || 'none'; counts[k] = (counts[k] || 0) + 1; });
  const order = [...Object.keys(LANG_LABELS_JA), 'none'].filter(k => counts[k]).concat(Object.keys(counts).filter(k => k !== 'none' && !LANG_LABELS_JA[k]));
  if (!order.length) return null;
  return (
    <div className="-mt-1 mb-3 flex flex-wrap items-center gap-1.5 text-xs text-gray-600" data-testid={`${testId}-lang-counts`}>
      <span className="text-gray-500">{label}：</span>
      {order.map(k => (
        <span key={k} className="px-1.5 py-0.5 rounded bg-gray-100 border border-gray-200 whitespace-nowrap" data-testid={`${testId}-lang-count-${k}`}>
          {k === 'none' ? '未設定' : (LANG_LABELS_JA[k] || k)} <span className="font-semibold text-gray-800">{counts[k]}</span>
        </span>
      ))}
    </div>
  );
};
export const LANG_FILTER_OPTIONS = [...Object.entries(LANG_LABELS_JA).map(([value, label]) => ({ value, label })), { value: 'none', label: '未設定' }];

const GuidesTab = ({
  trainingGuides,
  formatDateShort, getAuthHeaders, fetchData,
  onEditGuide, t, courses = [], applications = [], language = 'ja',
}) => {
  const guideLangCode = (g) => {
    const app = applications.find(a => a.id === g.applicationId);
    const nn = (v) => (v || '').replace(/[\s　]/g, '');
    const p = (app?.participants || []).find(x => nn(x?.name) && nn(x?.name) === nn(g.userName));
    return p?.language || app?.language || '';
  };
  const guideLanguage = (g) => { const code = guideLangCode(g); return LANG_LABELS_JA[code] || code; };
  const guideCourseOption = (g) => g.courseOption || applications.find(a => a.id === g.applicationId)?.courseOption || '';
  const sortedGuides = [...trainingGuides].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const filter = useFilteredList(
    sortedGuides,
    ['userName', 'userEmail', 'courseName', 'venue'],
    [{ key: 'status', label: t('admin.common.status'), options: [
      { value: 'sent', label: t('admin.certificates.sent') },
      { value: 'pending', label: t('admin.certificates.unsent') },
    ] },
    { key: 'language', label: '言語', get: g => guideLangCode(g) || 'none', options: LANG_FILTER_OPTIONS },
    { key: 'courseOption', label: 'コース', get: g => guideCourseOption(g) || 'none', options: [
      { value: 'A', label: 'Aコース' },
      { value: 'B', label: 'Bコース' },
      { value: 'C', label: 'Cコース' },
      { value: 'none', label: 'コース指定なし' },
    ] }]
  );
  const pager = usePagedList(filter.result);
  return (
  <TabsContent value="guides" className="p-4">
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">{t('admin.guides.title')}</h3>
      <ListToolbar filter={filter} testId="guides" placeholder="氏名・メール・講習・会場で検索" />
      <LangCounts codes={filter.result.map(guideLangCode)} label="言語別件数" testId="guides" />
      {pager.total === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <FileCheck className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>講習案内はまだありません</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('admin.common.name')}</TableHead>
              <TableHead>言語</TableHead>
              <TableHead>{t('admin.certificates.courseName')}</TableHead>
              <TableHead>{t('admin.guides.venue')}</TableHead>
              <TableHead>{t('admin.common.status')}</TableHead>
              <TableHead>{t('admin.common.dateTime')}</TableHead>
              <TableHead className="text-right">{t('admin.common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pager.paged.map(g => (
              <TableRow key={g.id}>
                <TableCell className="font-medium">{g.userName}</TableCell>
                <TableCell data-testid={`guide-language-${g.id}`}>
                  {guideLanguage(g) ? <span className="text-[10px] px-1 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200 whitespace-nowrap">{guideLanguage(g)}</span> : '-'}
                </TableCell>
                <TableCell>
                  {guideCourseOption(g) && (
                    <span className="mr-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#e6f6ec] text-[#00A651] border border-[#00A651]/30" data-testid={`guide-course-option-${g.id}`}>{guideCourseOption(g)}コース</span>
                  )}
                  {(() => { const cr = courses.find(c => c.id === g.courseId) || courses.find(c => c.name?.ja === g.courseName); return cr?.name?.[language] || cr?.name?.ja || g.courseName; })()}
                </TableCell>
                <TableCell>{g.venue}</TableCell>
                <TableCell>
                  <Badge className={g.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                    {g.status === 'sent' ? t('admin.certificates.sent') : t('admin.certificates.unsent')}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm">{formatDateShort(g.createdAt)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => window.open(`${API}/admin/training-guides/${g.id}/pdf`, '_blank')}>
                      <FileText className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" data-testid={`guide-edit-${g.id}`} title="編集" onClick={() => onEditGuide && onEditGuide(g)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-blue-600" onClick={async () => {
                      if (!window.confirm(`${g.userName}?`)) return;
                      try {
                        await axios.post(`${API}/admin/training-guides/${g.id}/send`, {}, { headers: getAuthHeaders() });
                        toast.success('講習案内を送信しました');
                        fetchData();
                      } catch (e) { toast.error(e.response?.data?.detail || '送信に失敗しました'); }
                    }}><Mail className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={async () => {
                      if (!window.confirm('この講習案内を削除しますか？')) return;
                      try {
                        await axios.delete(`${API}/admin/training-guides/${g.id}`, { headers: getAuthHeaders() });
                        toast.success('削除しました');
                        fetchData();
                      } catch { toast.error('削除に失敗しました'); }
                    }}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <ListPager pager={pager} testId="guides" />
    </div>
  </TabsContent>
  );
};

const GuidePatternsTab = ({
  guideTemplates, settings, courses,
  setEditingTemplate, setGuideTemplateForm, setGuideTemplateDialogOpen,
  getAuthHeaders, fetchData, t, language,
}) => {
  const patternFilter = useFilteredList(guideTemplates, ['title', 'venue', 'venueAddress']);
  const patternPager = usePagedList(patternFilter.result);
  const getLocalName = (title) => {
    if (!title || !courses) return title || '';
    const course = courses.find(c => c.name?.ja === title);
    if (course?.name && typeof course.name === 'object') {
      return course.name[language] || course.name.ja || title;
    }
    return title;
  };
  return (
  <TabsContent value="guidePatterns" className="p-4">
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">{t('admin.guides.title')}</h3>
        <Button className="bg-[#00A651] hover:bg-[#008a43]" data-testid="add-guide-template-btn"
          onClick={() => {
            setEditingTemplate(null);
            setGuideTemplateForm({ title: '', venue: settings?.organizationName || '', venueAddress: settings?.address || '', meetingTime: '', itemsToBring: '', notes: '', mapUrl: '', additionalInfo: '', timetable: [], courseOption: '' });
            setGuideTemplateDialogOpen(true);
          }}>
          <Plus className="h-4 w-4 mr-1" />
          {t('admin.guides.addPattern')}
        </Button>
      </div>
      <ListToolbar filter={patternFilter} testId="guide-patterns" placeholder="パターン名・会場で検索" />
      {patternPager.total === 0 ? (
        <div className="text-center py-8 text-gray-500"><BookOpen className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>{t('admin.guides.addPattern')}</p></div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {patternPager.paged.map(tpl => (
            <div key={tpl.id} className="border rounded-lg p-3 hover:border-[#00A651]/50 hover:shadow-md transition-all group relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-[#e6f6ec] flex items-center justify-center flex-shrink-0">
                  <BookOpen className="h-4 w-4 text-[#00A651]" />
                </div>
                <h4 className="font-medium text-gray-900 text-sm leading-tight line-clamp-2">{getLocalName(tpl.title)}</h4>
                {tpl.courseOption && (
                  <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#e6f6ec] text-[#00A651] border border-[#00A651]/30" data-testid={`guide-pattern-course-option-${tpl.id}`}>{tpl.courseOption}コース</span>
                )}
              </div>
              <div className="space-y-1 text-[11px] text-gray-500">
                {tpl.venue && <div className="flex items-center gap-1"><MapPin className="h-3 w-3 flex-shrink-0" /><span className="truncate">{tpl.venue}</span></div>}
                {(() => { const mt = patternMeetingTime(tpl); return mt ? <div className="flex items-center gap-1"><Clock className="h-3 w-3 flex-shrink-0" /><span>集合 {mt}</span></div> : null; })()}
              </div>
              <div className="flex gap-1 mt-2 pt-2 border-t">
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => {
                  setEditingTemplate(tpl);
                  setGuideTemplateForm({
                    title: tpl.title || '', venue: tpl.venue || '', venueAddress: tpl.venueAddress || '',
                    meetingTime: tpl.meetingTime || '', itemsToBring: tpl.itemsToBring || '',
                    notes: tpl.notes || '', mapUrl: tpl.mapUrl || '', additionalInfo: tpl.additionalInfo || '',
                    timetable: tpl.timetable || [], courseOption: tpl.courseOption || ''
                  });
                  setGuideTemplateDialogOpen(true);
                }}><Edit className="h-3.5 w-3.5 mr-1" />{t('admin.common.edit')}</Button>
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-red-500 hover:text-red-700" onClick={async () => {
                  if (!window.confirm(t('admin.common.delete') + '?')) return;
                  try {
                    await axios.delete(`${API}/admin/guide-templates/${tpl.id}`, { headers: getAuthHeaders() });
                    toast.success(t('admin.common.deleted'));
                    fetchData();
                  } catch { toast.error(t('admin.common.deleteFailed')); }
                }}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <ListPager pager={patternPager} testId="guide-patterns" />
    </div>
  </TabsContent>
  );
};

export { GuidesTab, GuidePatternsTab };
export default GuidesTab;
